<template>
  <NuxtLayout>
    <div class="min-h-screen bg-white text-purple-950">
      <AppHeader
        :mode="mode"
        @update:mode="onModeChange"
        @search-click="() => {}"
        @menu-click="() => {}"
        @login="onHeaderLogin"
        @how-it-works-click="() => {}"
      />

      <section class="w-full px-4 pb-6 pt-10 sm:px-6">
        <div
          class="relative mx-auto h-56 w-full max-w-[1366px] overflow-hidden rounded-2xl sm:h-64"
        >
          <div
            class="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-700"
            aria-hidden="true"
          ></div>
          <div class="absolute inset-0 bg-black/20" aria-hidden="true"></div>
          <div
            class="relative z-10 flex h-full flex-col items-center justify-center space-y-3 px-4 py-10 text-center sm:px-6 sm:py-14"
          >
            <span class="text-4xl">📜</span>
            <h1 class="text-3xl font-bold text-white sm:text-4xl md:text-5xl">
              {{ $t("terms.title") }}
            </h1>
            <p class="text-sm text-white/80 sm:text-base">
              {{ $t("terms.lastUpdated") }}
            </p>
          </div>
        </div>
      </section>

      <section class="w-full px-4 py-12">
        <div class="mx-auto w-full max-w-4xl space-y-6">
          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section1Title") }}
            </h2>
            <p class="mb-3 leading-relaxed text-gray-700">
              {{ $t("terms.section1P1", { platform: LEGAL_PLATFORM_NAME }) }}
            </p>
            <p class="mb-3 leading-relaxed text-gray-700">
              {{ $t("terms.section1P2") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section1P3Intro") }}
            </p>
            <div
              class="border-l-2 border-purple-200 pl-4 leading-relaxed text-gray-700"
            >
              <p>{{ LEGAL_OPERATOR_NAME }}</p>
              <p>{{ LEGAL_OPERATOR_ADDRESS }}</p>
              <p>{{ LEGAL_OPERATOR_CITY }}, Austria</p>
              <p>{{ $t("terms.section1OperatorSuffix") }}</p>
            </div>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section2Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section2P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section2P2") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section2P3") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section2P4") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section3Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section3P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section3P2") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section3P3") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section3P4") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section4Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section4P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section4P2") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section4P3") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section5Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section5P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section5P2") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section5P3") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section6Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section6P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section6P2") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section6P3") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section6P4") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section7Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section7P1") }}
            </p>
            <ul class="mb-3 list-disc space-y-1 pl-6 text-gray-700">
              <li>{{ $t("terms.section7Bullet1") }}</li>
              <li>{{ $t("terms.section7Bullet2") }}</li>
              <li>{{ $t("terms.section7Bullet3") }}</li>
              <li>{{ $t("terms.section7Bullet4") }}</li>
              <li>{{ $t("terms.section7Bullet5") }}</li>
            </ul>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section7P2") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section7P3") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section8Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section8P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section8P2") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section8P3") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section9Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section9P1") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section9P2") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section10Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section10P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section10P2") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section10P3") }}
            </p>
            <ul class="mb-3 list-disc space-y-1 pl-6 text-gray-700">
              <li>{{ $t("terms.section10Bullet1") }}</li>
              <li>{{ $t("terms.section10Bullet2") }}</li>
              <li>{{ $t("terms.section10Bullet3") }}</li>
              <li>{{ $t("terms.section10Bullet4") }}</li>
              <li>{{ $t("terms.section10Bullet5") }}</li>
            </ul>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section10P4") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section11Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section11P1") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section11P2") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section12Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section12P1") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section12P2") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section13Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section13P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section13P2") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section13P3") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section14Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section14P1") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section14P2") }}
            </p>
          </div>

          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 md:p-8"
          >
            <h2 class="mb-4 text-xl font-bold md:text-2xl">
              {{ $t("terms.section15Title") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section15P1") }}
            </p>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ $t("terms.section15P2") }}
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("terms.section15P3") }}
            </p>
          </div>

          <!-- <div class="bg-purple-50 rounded-3xl p-6 md:p-8 border border-purple-200">
                        <p class="font-semibold text-purple-900 mb-2">{{ $t('terms.noteTitle') }}</p>
                        <p class="text-purple-900/80">{{ $t('terms.noteBody') }}</p>
                    </div> -->
        </div>
      </section>

      <section class="w-full px-4 pb-12">
        <div class="mx-auto w-full max-w-[1366px]">
          <FooterMainPage
            @primary="onFooterPrimary"
            @secondary="onFooterSecondary"
          />
        </div>
      </section>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import AppHeader from "~/components/header/AppHeader.vue";
import FooterMainPage from "~/components/FooterMainPage.vue";

type Mode = "event" | "establishment";
const mode = ref<Mode>("event");
const localePath = useLocalePath();
const { t } = useI18n();
const {
  APP_URL,
  LEGAL_PLATFORM_NAME,
  LEGAL_OPERATOR_NAME,
  LEGAL_OPERATOR_ADDRESS,
  LEGAL_OPERATOR_CITY,
} = usePublicUrls();

const onModeChange = (m: Mode) => {
  mode.value = m;
};
const onHeaderLogin = () => {
  window.location.href = `${APP_URL}/`;
};
const onFooterPrimary = () => {
  navigateTo(localePath("/"));
};
const onFooterSecondary = () => {
  navigateTo(localePath("/"));
};

useSeoMeta({
  title: computed(() => `${t("terms.title")} - Upfest`),
  description: () => t("terms.description"),
});
useStaticPageSeo("/terms");
</script>
